import type { Data } from '@dnd-kit/abstract'
import { SortableDraggable } from '@dnd-kit/dom/sortable'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { CheckIcon, DotsSixVerticalIcon, PlusIcon, XIcon } from '@phosphor-icons/react'
import { ComponentProps, useState } from 'react'

import { Button } from '@/components/ui/button'
import { ErrorMessage } from '@/components/ui/error-message'
import { Icon } from '@/components/ui/icon'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'

import * as S from './editable-list.styled'
import { EditableListItem, EditableListProps } from './editable-list.types'
import { SortableRow } from './sortable-row'

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>

export const EditableList = ({
  title,
  description,
  items,
  addLabel = 'Add',
  error,
  maxLength,
  validate,
  onAdd,
  onRename,
  onDelete,
  onReorder,
}: EditableListProps) => {
  const [mode, setMode] = useState<'add' | 'rename' | 'static'>('static')
  const [currentItem, setCurrentItem] = useState<EditableListItem | null>(null)
  const [inputValue, setInputValue] = useState('')

  const pinnedItems = items.filter((i) => i.pinned)
  const sortableItems = items.filter((i) => !i.pinned)

  const isRenameOrAddMode = mode !== 'static'

  const trimmedValue = inputValue.trim()
  const inputError = trimmedValue
    ? validate?.(trimmedValue, mode === 'rename' ? currentItem?.id : null)
    : undefined
  const isConfirmDisabled = !trimmedValue || !!inputError

  const handleInputChange = (value: string) => {
    setInputValue(value)
  }

  const handleCancelEdit = () => {
    setMode('static')
    setCurrentItem(null)
    setInputValue('')
  }

  const handleAddClick = () => {
    setMode('add')
    setCurrentItem(null)
    setInputValue('')
  }

  const handleConfirmAdd = () => {
    if (isConfirmDisabled) return
    onAdd(trimmedValue)
    handleCancelEdit()
  }

  const handleEditStart = (item: EditableListItem) => {
    setMode('rename')
    setCurrentItem(item)
    setInputValue(item.name)
  }

  const handleConfirmRename = () => {
    if (mode !== 'rename' || !currentItem || isConfirmDisabled) return
    const name = trimmedValue
    handleCancelEdit()
    onRename(currentItem, name)
  }

  const handleDragEnd: DragEndHandler = (event) => {
    const { canceled, operation } = event
    if (canceled) return

    const { source } = operation
    if (!source || !isSortable(source)) return

    const sortableSource = source as SortableDraggable<Data>
    const sourceIndex = sortableSource.initialIndex
    const targetIndex = sortableSource.index
    if (sourceIndex === targetIndex) return

    const reordered = [...sortableItems]
    const [removed] = reordered.splice(sourceIndex, 1)
    reordered.splice(targetIndex, 0, removed)

    onReorder(reordered)
  }

  return (
    <S.Root>
      <S.Header>
        <S.Title>
          <Typography size="text-medium" weight="medium">
            {title}
          </Typography>
        </S.Title>
        <Button use="secondary" size="small" disabled={isRenameOrAddMode} onClick={handleAddClick}>
          {addLabel}
          <Icon icon={PlusIcon} />
        </Button>
      </S.Header>

      {description && (
        <Typography size="text-small" color="black700">
          {description}
        </Typography>
      )}

      <S.List>
        {pinnedItems.map((item) => (
          <S.Row key={item.id}>
            <S.DragHandle $disabled>
              <Icon icon={DotsSixVerticalIcon} />
            </S.DragHandle>
            <S.ItemName>{item.name}</S.ItemName>
          </S.Row>
        ))}

        <DragDropProvider onDragEnd={handleDragEnd}>
          {sortableItems.map((item, index) => (
            <SortableRow
              key={item.id}
              item={item}
              index={index}
              isThisRowEditing={mode === 'rename' && currentItem?.id === item.id}
              isAnyRowEditing={isRenameOrAddMode}
              inputValue={inputValue}
              inputError={mode === 'rename' && currentItem?.id === item.id ? inputError : undefined}
              isConfirmDisabled={isConfirmDisabled}
              maxLength={maxLength}
              onEditStart={() => handleEditStart(item)}
              onDeleteClick={() => onDelete(item)}
              onInputChange={handleInputChange}
              onInputCancel={handleCancelEdit}
              onInputConfirm={handleConfirmRename}
            />
          ))}

          {mode === 'add' && (
            <S.Row>
              <S.DragHandle $disabled>
                <Icon icon={DotsSixVerticalIcon} />
              </S.DragHandle>
              <S.InputWrapper>
                <TextInput
                  autoFocus
                  size="medium"
                  value={inputValue}
                  error={inputError}
                  maxLength={maxLength}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConfirmAdd()
                    if (e.key === 'Escape') handleCancelEdit()
                  }}
                />
              </S.InputWrapper>
              <S.Actions>
                <Button onlyBox use="ghost" size="small" onClick={handleCancelEdit}>
                  <Icon icon={XIcon} color="red500" />
                </Button>
                <Button
                  onlyBox
                  use="ghost"
                  size="small"
                  disabled={isConfirmDisabled}
                  onClick={handleConfirmAdd}
                >
                  <Icon icon={CheckIcon} color="green500" />
                </Button>
              </S.Actions>
            </S.Row>
          )}
        </DragDropProvider>
      </S.List>

      <ErrorMessage>{error}</ErrorMessage>
    </S.Root>
  )
}
